/* ==========================================================================
   In8te — theme.js
   Build-free ES module. Vanilla web components, no dependencies.
   - Cart: single source of truth, pub/sub via CustomEvents on document.
   - Components register as custom elements so they survive section reloads
     in the theme editor.
   - Everything degrades: without JS, forms POST to Shopify normally.
   ========================================================================== */

const Theme = window.Theme || { routes: {}, settings: {}, strings: {} };

/* Translations edited in the Shopify admin can arrive HTML-escaped
   (&#39; etc.). Decode every UI string once so textContent paths never
   print raw entities. */
{
  const ta = document.createElement('textarea');
  Object.keys(Theme.strings || {}).forEach((k) => {
    if (typeof Theme.strings[k] === 'string' && Theme.strings[k].includes('&')) {
      ta.innerHTML = Theme.strings[k];
      Theme.strings[k] = ta.value;
    }
  });
}

/* ----------------------------- Utilities ------------------------------ */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Garment size order for anything built in JS (popup size chips etc.):
   XS → S → M → L → XL …, numeric sizes ascending, unknown values last. */
const SIZE_ORDER_ALL = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL'];
function sizeRank(label) {
  const s = String(label || '').toUpperCase().trim();
  const i = SIZE_ORDER_ALL.indexOf(s);
  if (i !== -1) return i;
  const n = parseFloat(s);
  return Number.isFinite(n) ? 50 + n : 200;
}

export function formatMoney(cents) {
  if (typeof cents === 'string') cents = cents.replace(/[^0-9.-]/g, '') * 100;
  const format = Theme.settings.moneyFormat || '${{amount}}';
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  const type = match ? match[1] : 'amount';

  function delimit(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) return '0';
    number = (number / 100).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  let value = '';
  switch (type) {
    case 'amount': value = delimit(cents, 2, ',', '.'); break;
    case 'amount_no_decimals': value = delimit(cents, 0, ',', '.'); break;
    case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': value = delimit(cents, 2, "'", '.'); break;
    case 'amount_no_decimals_with_space_separator': value = delimit(cents, 0, ' ', ','); break;
    case 'amount_with_space_separator': value = delimit(cents, 2, ' ', ','); break;
    default: value = delimit(cents, 2, ',', '.');
  }
  return format.replace(placeholder, value);
}

const cssEscape = (s) => (window.CSS?.escape ? window.CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'));

const debounce = (fn, wait = 200) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

const emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));

async function fetchJSON(url, options) {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Request failed'), { data, status: res.status });
  return data;
}

/* ----------------------------- Scroll lock ---------------------------- */
const overlayEl = () => document.querySelector('[data-page-overlay]');
let lockCount = 0;
export function lockScroll(showOverlay = true) {
  lockCount += 1;
  document.body.classList.add('no-scroll');
  const ov = overlayEl();
  if (showOverlay && ov) {
    ov.hidden = false;
    requestAnimationFrame(() => ov.classList.add('is-visible'));
  }
}
export function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;
  document.body.classList.remove('no-scroll');
  const ov = overlayEl();
  if (ov) {
    ov.classList.remove('is-visible');
    setTimeout(() => { if (!ov.classList.contains('is-visible')) ov.hidden = true; }, 350);
  }
}

/* ----------------------------- Focus trap ----------------------------- */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
function trapFocus(container, e) {
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
  else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
}

/* ----------------------------- Toast ----------------------------------- */
let toastEl;
let toastTimer;
function toast(message) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast';
    toastEl.setAttribute('role', 'status');
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  requestAnimationFrame(() => toastEl.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
}
Theme.toast = toast;
Theme.formatMoney = formatMoney;
Theme.lockScroll = lockScroll;
Theme.unlockScroll = unlockScroll;

/* ----------------------------- Cart API -------------------------------- */
export const Cart = {
  state: null,

  async get() {
    this.state = await fetchJSON(`${Theme.routes.cart || '/cart'}.js`);
    return this.state;
  },

  async add(items, { open = true, recommendHandles = null, sourceId = null } = {}) {
    const payload = Array.isArray(items) ? items : [items];
    const data = await fetchJSON(`${Theme.routes.cartAdd || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: payload }),
    });
    const cart = await this.get();
    emit('cart:updated', { cart });
    const added = data.items ? data.items[0] : data;
    if (open) emit('cart:added', { item: added, cart, recommendHandles, sourceId });
    return { added, cart };
  },

  async change(key, quantity) {
    const cart = await fetchJSON(`${Theme.routes.cartChange || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    this.state = cart;
    emit('cart:updated', { cart });
    return cart;
  },
};
Theme.Cart = Cart;

document.addEventListener('cart:updated', (e) => {
  const count = e.detail.cart?.item_count ?? 0;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.classList.toggle('is-empty', count === 0);
  });
});

/* ----------------------------- Wishlist -------------------------------- */
const Wishlist = {
  KEY: 'in8te_wishlist',
  read() {
    try { return JSON.parse(localStorage.getItem(this.KEY)) || []; } catch { return []; }
  },
  write(list) {
    localStorage.setItem(this.KEY, JSON.stringify(list));
    this.sync();
    emit('wishlist:updated', { list });
  },
  has(handle) { return this.read().includes(handle); },
  toggle(handle) {
    const list = this.read();
    const i = list.indexOf(handle);
    if (i >= 0) list.splice(i, 1); else list.push(handle);
    this.write(list);
    return i < 0;
  },
  sync() {
    const list = this.read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const active = list.includes(btn.dataset.wishlistToggle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active);
      btn.setAttribute('aria-label', active ? Theme.strings.wishlistRemove : Theme.strings.wishlistAdd);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = list.length;
      el.classList.toggle('is-empty', list.length === 0);
    });
  },
};
Theme.Wishlist = Wishlist;

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  const added = Wishlist.toggle(btn.dataset.wishlistToggle);
  if (added) {
    btn.classList.add('is-pop');
    setTimeout(() => btn.classList.remove('is-pop'), 450);
  }
});

/* ----------------------------- Reveal on scroll ------------------------ */
let revealObserver;
function initReveals(root = document) {
  if (!document.body.classList.contains('reveals-enabled') || reducedMotion.matches) {
    root.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.filter((en) => en.isIntersecting).forEach((en, i) => {
        en.target.style.setProperty('--reveal-delay', `${Math.min(i, 5) * 70}ms`);
        en.target.classList.add('is-revealed');
        revealObserver.unobserve(en.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -4% 0px' });
  }
  root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
    // Anything within the first ~1.6 screens is shown instantly — the intro
    // (hero + the first banners) must never fade in on arrival
    if (root === document && el.getBoundingClientRect().top < window.innerHeight * 1.6) {
      el.style.transition = 'none';
      el.classList.add('is-revealed');
      return;
    }
    revealObserver.observe(el);
  });
}

/* ----------------------------- Video autoplay when visible -------------- */
let videoObserver;
function initVisibleVideos(root = document) {
  if (!videoObserver) {
    videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const video = en.target;
        if (en.isIntersecting && !video.closest('[data-video-paused]') && !reducedMotion.matches) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.2 });
  }
  root.querySelectorAll('video[data-autoplay-visible], .ig-post__media video, .tech-row__media video, .fcs__media video, .frow__media video, .tsys__media video').forEach((v) => videoObserver.observe(v));
}

/* ----------------------------- Accordion animation ---------------------- */
document.addEventListener('click', (e) => {
  const summary = e.target.closest('summary.accordion__summary');
  if (!summary) return;
  const details = summary.parentElement;
  const content = details.querySelector('.accordion__content');
  if (!content || reducedMotion.matches) return;
  e.preventDefault();
  if (details.dataset.animating) return;
  details.dataset.animating = '1';
  if (details.open) {
    const anim = content.animate(
      [{ height: `${content.offsetHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: 260, easing: 'ease' }
    );
    anim.onfinish = () => { details.open = false; delete details.dataset.animating; };
  } else {
    details.open = true;
    const h = content.offsetHeight;
    const anim = content.animate(
      [{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }],
      { duration: 300, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
    anim.onfinish = () => delete details.dataset.animating;
  }
});

/* ----------------------------- <quantity-input> ------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty]');
      if (!btn || !this.input) return;
      e.preventDefault();
      const step = btn.dataset.qty === 'up' ? 1 : -1;
      const min = parseInt(this.input.min || 0, 10);
      const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
      if (next !== parseInt(this.input.value, 10)) {
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* ----------------------------- <modal-dialog> ---------------------------- */
class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-modal-close]') || e.target.classList.contains('modal__backdrop')) this.close();
    });
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); e.key === 'Tab' && trapFocus(this, e); };
  }
  open(trigger) {
    this.trigger = trigger;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    lockScroll(false);
    document.addEventListener('keydown', this.onKey);
    (this.querySelector('[data-modal-focus]') || this.querySelector(FOCUSABLE))?.focus();
  }
  close() {
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKey);
    unlockScroll();
    setTimeout(() => { this.hidden = true; this.trigger?.focus(); }, 320);
  }
}
customElements.define('modal-dialog', ModalDialog);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-modal-open]');
  if (!opener) return;
  const modal = document.getElementById(opener.dataset.modalOpen);
  if (modal && modal.open) { e.preventDefault(); modal.open(opener); }
});

/* ----------------------------- <scroll-carousel> ------------------------- */
class ScrollCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.carousel__viewport');
    this.track = this.querySelector('.carousel__track');
    if (!this.viewport || !this.track) return;
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.fill = this.querySelector('.carousel__progress-fill');
    this.counter = this.querySelector('[data-carousel-counter]');

    this.prevBtn?.addEventListener('click', () => this.page(-1));
    this.nextBtn?.addEventListener('click', () => this.page(1));
    this.viewport.addEventListener('scroll', () => {
      this.update();
      // reveals the scrollbar while scrolling (mobile has no hover)
      this.classList.add('is-scrolling');
      clearTimeout(this.scrollFadeTimer);
      this.scrollFadeTimer = setTimeout(() => this.classList.remove('is-scrolling'), 700);
    }, { passive: true });
    window.addEventListener('resize', debounce(() => this.update(), 150));
    this.initDrag();
    this.progress = this.querySelector('.carousel__progress');
    if (this.progress) this.initProgressSeek();
    this.update();
  }

  /* The progress bar doubles as a scrollbar. Like a native scrollbar:
     dragging moves the content RELATIVE to where it was (no jump to the
     pointer), a plain click glides smoothly to the clicked position. */
  initProgressSeek() {
    const targetLeft = (clientX) => {
      const rect = this.progress.getBoundingClientRect();
      if (!rect.width) return null;
      const frac = Math.max(0.08, this.viewport.clientWidth / this.viewport.scrollWidth);
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const adjusted = Math.min(1, Math.max(0, (ratio - frac / 2) / (1 - frac)));
      return adjusted * (this.viewport.scrollWidth - this.viewport.clientWidth);
    };
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    this.progress.addEventListener('pointerdown', (e) => {
      dragging = true;
      moved = false;
      startX = e.clientX;
      startLeft = this.viewport.scrollLeft;
      this.progress.setPointerCapture(e.pointerId);
      // disables scroll-snap while scrubbing so the motion is fluid
      this.viewport.classList.add('is-dragging');
      e.preventDefault();
    });
    this.progress.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      moved = true;
      const rect = this.progress.getBoundingClientRect();
      const frac = Math.max(0.08, this.viewport.clientWidth / this.viewport.scrollWidth);
      const thumbTravel = rect.width * (1 - frac);
      if (thumbTravel <= 0) return;
      const scrollable = this.viewport.scrollWidth - this.viewport.clientWidth;
      this.viewport.scrollLeft = startLeft + (e.clientX - startX) * (scrollable / thumbTravel);
    });
    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      this.viewport.classList.remove('is-dragging');
      const iw = this.itemWidth();
      let left = moved ? this.viewport.scrollLeft : targetLeft(e.clientX);
      if (left == null) left = this.viewport.scrollLeft;
      this.viewport.scrollTo({
        left: Math.round(left / iw) * iw,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };
    this.progress.addEventListener('pointerup', end);
    this.progress.addEventListener('pointercancel', end);
  }

  itemWidth() {
    const item = this.track.children[0];
    if (!item) return this.viewport.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  page(dir) {
    const iw = this.itemWidth();
    const perPage = Math.max(1, Math.floor(this.viewport.clientWidth / iw));
    this.viewport.scrollBy({ left: dir * iw * perPage, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  scrollToIndex(i) {
    this.viewport.scrollTo({ left: this.itemWidth() * i, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  update() {
    const vp = this.viewport;
    const max = vp.scrollWidth - vp.clientWidth;
    const progress = max > 0 ? vp.scrollLeft / max : 0;
    if (this.prevBtn) this.prevBtn.disabled = vp.scrollLeft <= 4;
    if (this.nextBtn) this.nextBtn.disabled = vp.scrollLeft >= max - 4;
    if (this.fill) {
      const frac = Math.max(0.08, vp.clientWidth / vp.scrollWidth);
      this.fill.style.width = `${frac * 100}%`;
      this.fill.style.marginLeft = `${progress * (1 - frac) * 100}%`;
    }
    const index = Math.round(vp.scrollLeft / this.itemWidth());
    if (this.counter) {
      this.counter.textContent = `${String(Math.min(index + 1, this.track.children.length)).padStart(2, '0')} / ${String(this.track.children.length).padStart(2, '0')}`;
    }
    if (index !== this.lastIndex) {
      this.lastIndex = index;
      this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index } }));
    }
  }

  initDrag() {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    const THRESHOLD = 12; // px of travel before a press becomes a drag
    let startX = 0;
    let startScroll = 0;
    let pressed = false;
    let dragging = false;

    // Native image/link dragging would hijack the swipe
    this.viewport.addEventListener('dragstart', (e) => e.preventDefault());

    this.viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Text fields keep native behaviour; links/buttons are fine to drag
      // from — a plain click still lands because the post-drag click is
      // swallowed only after real movement.
      if (e.target.closest('input, select, textarea')) return;
      pressed = true;
      dragging = false;
      startX = e.clientX;
      startScroll = this.viewport.scrollLeft;
    });

    window.addEventListener('pointermove', (e) => {
      if (!pressed) return;
      const dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) > THRESHOLD) {
        dragging = true;
        this.viewport.classList.add('is-dragging');
      }
      if (dragging) this.viewport.scrollLeft = startScroll - dx;
    });

    window.addEventListener('pointerup', () => {
      if (!pressed) return;
      pressed = false;
      if (!dragging) return; // plain click — leave it completely alone
      dragging = false;
      this.viewport.classList.remove('is-dragging');
      const iw = this.itemWidth();
      this.viewport.scrollTo({ left: Math.round(this.viewport.scrollLeft / iw) * iw, behavior: 'smooth' });
      // Swallow only the click that ends this drag, then auto-disarm so it
      // can never eat a later genuine click.
      const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
      this.viewport.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => this.viewport.removeEventListener('click', swallow, { capture: true }), 200);
    });
  }
}
customElements.define('scroll-carousel', ScrollCarousel);

/* ----------------------------- <instagram-feed> --------------------------- */
/* Live posts via the Instagram Graph API (access token) or any JSON feed
   URL (Behold, Curator…). Section blocks stay as fallback content. */
class InstagramFeed extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.carousel__track');
    const token = (this.dataset.token || '').trim();
    const feedUrl = (this.dataset.feedUrl || '').trim();
    if (!this.track || (!token && !feedUrl)) return;
    this.limit = Math.min(Math.max(Number(this.dataset.limit) || 8, 1), 12);
    if (!this.track.children.length) {
      this.track.innerHTML = Array.from({ length: this.limit })
        .map(() => '<span class="ig-post"><span class="ig-post__media skeleton"></span></span>').join('');
    }
    this.load(token, feedUrl);
  }

  async load(token, feedUrl) {
    const src = feedUrl
      || `https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url,permalink&limit=${this.limit}&access_token=${encodeURIComponent(token)}`;
    // 30-minute cache keeps the section instant and API-friendly
    const key = `ig-feed:${src.slice(-32)}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < 30 * 60 * 1000 && cached.posts.length) {
        return this.render(cached.posts);
      }
    } catch { /* noop */ }
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`Instagram feed HTTP ${res.status}`);
      const posts = this.normalize(await res.json());
      if (!posts.length) throw new Error('Instagram feed returned no posts');
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), posts })); } catch { /* noop */ }
      this.render(posts);
    } catch (err) {
      console.warn('[instagram-feed]', err);
      // fetch failed — put the section blocks back if we replaced them with skeletons
      if (this.track.querySelector('.skeleton')) this.track.innerHTML = '';
    }
  }

  /* Accepts Graph API ({data:[…]}) and common feed-service shapes */
  normalize(data) {
    const list = Array.isArray(data) ? data : data.data || data.posts || data.items || [];
    return list
      .map((p) => ({
        url: p.permalink || p.url || p.link || '',
        type: String(p.media_type || p.mediaType || (p.isVideo ? 'VIDEO' : 'IMAGE')).toUpperCase(),
        src: p.media_url || p.mediaUrl || p.src || p.image || '',
        poster: p.thumbnail_url || p.thumbnailUrl || p.poster || '',
        caption: (p.caption || '').split('\n')[0].slice(0, 90),
      }))
      .filter((p) => p.src)
      .slice(0, this.limit);
  }

  render(posts) {
    const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const hoverIcon = this.querySelector('template[data-ig-icon-hover]')?.innerHTML || '';
    const playIcon = this.querySelector('template[data-ig-icon-play]')?.innerHTML || '';
    this.track.innerHTML = posts.map((p) => {
      const isVideo = p.type === 'VIDEO';
      const media = isVideo
        ? `<video class="ig-post__video" muted loop playsinline preload="metadata"${p.poster ? ` poster="${esc(p.poster)}"` : ''}><source src="${esc(p.src)}" type="video/mp4"></video>`
        : `<img src="${esc(p.src)}" alt="${esc(p.caption) || 'Instagram'}" loading="lazy">`;
      return `
        <a class="ig-post" href="${esc(p.url || '#')}" target="_blank" rel="noopener">
          <span class="ig-post__media">
            ${media}
            <span class="ig-post__hover" aria-hidden="true">${hoverIcon}</span>
            ${isVideo ? `<span class="ig-post__play" aria-hidden="true">${playIcon}</span>` : ''}
          </span>
        </a>`;
    }).join('');
    this.querySelector('scroll-carousel')?.update?.();
    // re-run reveal/video-autoplay observers over the new nodes
    document.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
  }
}
customElements.define('instagram-feed', InstagramFeed);

/* ----------------------------- <product-card> ----------------------------- */
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
class ProductCard extends HTMLElement {
  connectedCallback() {
    this.data = null;
    const json = this.querySelector('[data-card-json]');
    if (json) { try { this.data = JSON.parse(json.textContent); } catch { /* noop */ } }

    // Hover media (2nd photo / video) is shipped in a <template> and only
    // mounted on devices that can hover — phones never download it
    const media = this.querySelector('.product-card__media');
    const hoverTpl = this.querySelector('template[data-hover-media]');
    if (hoverTpl) {
      if (canHover.matches) hoverTpl.replaceWith(hoverTpl.content.cloneNode(true));
      else hoverTpl.remove();
    }

    // Hover video — plays only while hovering the image area, not the whole card
    const video = this.querySelector('[data-hover-video]');
    if (video && media) {
      media.addEventListener('mouseenter', () => { video.play().catch(() => {}); });
      media.addEventListener('mouseleave', () => { video.pause(); video.currentTime = 0; });
    }

    // Swatches — picking a color swaps the photo (and its hover photo) to that color
    this.activeVariantId = null;
    this.sel = {}; // chosen values for non-color options, keyed by option index
    this.querySelectorAll('[data-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        this.querySelectorAll('[data-swatch]').forEach((s) => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        this.activeVariantId = Number(swatch.dataset.variantId);
        const img = this.querySelector('.product-card__img.is-primary');
        if (swatch.dataset.image && img && img.tagName === 'IMG') {
          img.src = swatch.dataset.image;
          if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset;
        }
        const hover = this.querySelector('.product-card__img.is-hover');
        if (hover && hover.tagName === 'IMG') {
          if (swatch.dataset.hoverImage) {
            hover.src = swatch.dataset.hoverImage;
            if (swatch.dataset.hoverSrcset) hover.srcset = swatch.dataset.hoverSrcset;
            hover.hidden = false;
          } else if (swatch.dataset.image) {
            // no second photo of this color — don't hover-swap to a wrong color
            hover.hidden = true;
          }
        }
        if (swatch.dataset.url) {
          this.querySelectorAll('a.product-card__media-link, a.product-card__link').forEach((a) => { a.href = swatch.dataset.url; });
        }
        if (this.sizePanel && !this.sizePanel.hidden) this.renderPanel(this.panelGroups());
      });
    });

    // Quick add
    this.quickAddBtn = this.querySelector('[data-quick-add]');
    this.sizePanel = this.querySelector('[data-size-panel]');
    this.quickAddBtn?.addEventListener('click', (e) => { e.preventDefault(); this.quickAdd(); });
    this.querySelector('[data-size-close]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.sizePanel.hidden = true;
    });
    if (this.sizePanel) {
      // composedPath (not contains): re-rendered chips are detached from the
      // DOM by the time this runs, which would falsely read as "outside"
      document.addEventListener('click', (e) => {
        if (!this.sizePanel.hidden && !e.composedPath().includes(this)) this.sizePanel.hidden = true;
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.sizePanel.hidden) {
          this.sizePanel.hidden = true;
          this.quickAddBtn?.focus();
        }
      });
    }
  }

  baseVariant() {
    if (!this.data) return null;
    return this.data.variants.find((v) => v.id === this.activeVariantId) || this.data.variants[0];
  }

  // Variants matching a candidate value for group g, the active color and
  // whatever else is already selected — used for chip availability
  variantsFor(g, val, groups) {
    const colorIdx = this.data.colorOption - 1;
    const base = this.baseVariant();
    return this.data.variants.filter((v) =>
      v.options[g.index] === val
      && (colorIdx < 0 || !base || v.options[colorIdx] === base.options[colorIdx])
      && groups.every((o) => o.index === g.index || this.sel[o.index] == null || v.options[o.index] === this.sel[o.index]));
  }

  // The exact variant for the current color + picks; null while incomplete
  selectedVariant(groups) {
    const base = this.baseVariant();
    if (!base) return null;
    if (!groups.every((g) => this.sel[g.index] != null)) return null;
    const opts = base.options.slice();
    groups.forEach((g) => { opts[g.index] = this.sel[g.index]; });
    return this.matchVariant(opts) || null;
  }


  // Options offered in the panel: everything except color, skipping single-value options.
  // Sizes are sorted in garment order regardless of admin variant order.
  panelGroups() {
    const names = this.data.options || [];
    const colorIdx = this.data.colorOption - 1;
    const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL'];
    const rank = (v) => { const i = SIZE_ORDER.indexOf(String(v).toUpperCase()); return i === -1 ? 100 : i; };
    return names
      .map((name, index) => {
        const values = [];
        this.data.variants.forEach((v) => { if (!values.includes(v.options[index])) values.push(v.options[index]); });
        values.sort((a, b) => rank(a) - rank(b)); // stable: unknown values keep admin order
        return { name, index, values };
      })
      .filter((g) => g.index !== colorIdx && g.values.length > 1);
  }

  matchVariant(sel) {
    return this.data.variants.find((v) => v.options.every((val, i) => val === sel[i]));
  }

  quickAdd() {
    // No dead clicks: without usable data the button falls back to the product page
    const productUrl = this.querySelector('a.product-card__link')?.href
      || this.querySelector('a.product-card__media-link')?.href;
    if (!this.data || !this.data.variants.length) {
      if (productUrl) window.location.href = productUrl;
      return;
    }
    if (this.data.variants.length === 1) return this.add(this.data.variants[0].id);

    const groups = this.panelGroups();
    if (!groups.length) {
      // Only color varies — the active swatch already picks the exact variant
      const v = this.baseVariant();
      if (v && v.available) return this.add(v.id);
      if (productUrl) window.location.href = productUrl;
      return;
    }

    this.preselectDefaults(groups);

    // Preferred UX: Salomon-style slide-in side panel with all options
    const drawer = document.querySelector('quick-add-drawer');
    if (drawer) {
      drawer.open(this);
      return;
    }

    // Fallback: small in-card panel. The bag only toggles it — adding to
    // cart always happens via the panel's add button.
    if (!this.sizePanel) {
      if (productUrl) window.location.href = productUrl;
      return;
    }
    if (!this.sizePanel.hidden) {
      this.sizePanel.hidden = true;
      return;
    }
    this.renderPanel(groups);
    this.sizePanel.hidden = false;
    this.sizePanel.querySelector('.size-chip.is-active, .size-chip:not([disabled])')?.focus();
  }

  // Pre-select a sensible default for anything not chosen yet (selection
  // only — nothing is added until the add button is clicked)
  preselectDefaults(groups) {
    const colorIdx = this.data.colorOption - 1;
    const base = this.baseVariant();
    const pool = colorIdx < 0 || !base
      ? this.data.variants
      : this.data.variants.filter((v) => v.options[colorIdx] === base.options[colorIdx]);
    const def = pool.find((v) => v.available) || pool[0] || this.data.variants[0];
    groups.forEach((g) => { if (this.sel[g.index] == null) this.sel[g.index] = def.options[g.index]; });
  }

  // Shared options UI (color row + option chips) — used by the in-card
  // panel and the quick-add side drawer. Chips only SELECT.
  renderOptionsInto(container, onChange) {
    const groups = this.panelGroups();

    // Selected color is shown (and changeable) — clicks delegate to the
    // card swatches so photos and state stay in sync
    const swatches = [...this.querySelectorAll('[data-swatch]')];
    const colorIdx = this.data.colorOption - 1;
    if (swatches.length && colorIdx >= 0) {
      const groupEl = document.createElement('div');
      groupEl.className = 'product-card__opt-group';
      const label = document.createElement('p');
      label.className = 'product-card__opt-label';
      const activeName = swatches.find((s) => s.classList.contains('is-active'))?.title || '';
      label.textContent = activeName
        ? `${this.data.options[colorIdx]}: ${activeName}`
        : this.data.options[colorIdx];
      groupEl.appendChild(label);
      const row = document.createElement('div');
      row.className = 'product-card__size-row';
      swatches.forEach((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'product-card__opt-color';
        b.title = s.title;
        b.setAttribute('aria-label', s.title);
        if (s.classList.contains('is-active')) b.classList.add('is-active');
        const img = s.querySelector('img');
        const dot = s.querySelector('.product-card__swatch-dot');
        if (img) b.appendChild(img.cloneNode());
        else if (dot) b.appendChild(dot.cloneNode(true));
        b.addEventListener('click', () => { s.click(); onChange(); });
        row.appendChild(b);
      });
      groupEl.appendChild(row);
      container.appendChild(groupEl);
    }

    groups.forEach((g) => {
      const groupEl = document.createElement('div');
      groupEl.className = 'product-card__opt-group';
      if (groups.length > 1 || swatches.length) {
        const label = document.createElement('p');
        label.className = 'product-card__opt-label';
        label.textContent = g.name;
        groupEl.appendChild(label);
      }
      const row = document.createElement('div');
      row.className = 'product-card__size-row';
      g.values.forEach((val) => {
        const fits = this.variantsFor(g, val, groups);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'size-chip';
        chip.textContent = val;
        if (!fits.some((v) => v.available)) chip.disabled = true;
        if (this.sel[g.index] === val) chip.classList.add('is-active');
        chip.addEventListener('click', () => {
          // chips only select — adding happens on the add button
          this.sel[g.index] = val;
          onChange();
        });
        row.appendChild(chip);
      });
      groupEl.appendChild(row);
      container.appendChild(groupEl);
    });
  }

  renderPanel(groups) {
    const wrap = this.querySelector('[data-size-options]');
    if (!wrap) return;
    wrap.innerHTML = '';
    this.renderOptionsInto(wrap, () => this.renderPanel(groups));

    const chosen = this.selectedVariant(groups);
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'btn btn--primary btn--sm product-card__opt-add';
    confirm.textContent = Theme.strings.addToCart;
    confirm.disabled = !(chosen && chosen.available);
    confirm.addEventListener('click', () => { this.sizePanel.hidden = true; this.add(chosen.id); });
    wrap.appendChild(confirm);
  }

  async add(variantId) {
    this.quickAddBtn?.classList.add('is-loading');
    try {
      await Cart.add({ id: variantId, quantity: 1 }, { sourceId: Number(this.dataset.id) });
    } catch (err) {
      toast(err.message || Theme.strings.cartError);
    } finally {
      this.quickAddBtn?.classList.remove('is-loading');
    }
  }
}
customElements.define('product-card', ProductCard);

/* ----------------------------- <cart-remove-button> etc. handled on cart page --- */

/* ----------------------------- <cart-drawer> -------------------------------- */
/* One cart line (photo, title, variant, qty +/- , price, remove) — used by the
   cart drawer and by the added-to-cart popup. */
function cartLineHTML(item) {
  const esc = (s = '') => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const resize = (url, w) => (url ? url.replace(/(\.[a-z]{3,4})(\?|$)/i, `_${w}x$1$2`) : '');
  return `
      <div class="dline" data-line="${esc(item.key)}">
        <a class="dline__media" href="${esc(item.url)}" tabindex="-1">
          ${item.image ? `<img src="${resize(item.image, 200)}" alt="" loading="lazy" width="64" height="96">` : ''}
        </a>
        <div class="dline__info">
          <a class="dline__title" href="${esc(item.url)}">${esc(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="dline__variant">${esc(item.variant_title)}</span>` : ''}
          <div class="dline__row">
            <div class="dline__qty">
              <button type="button" data-dline-qty="${item.quantity - 1}" data-key="${esc(item.key)}" aria-label="${esc(Theme.strings.decrease || '−')}">${item.quantity === 1 ? '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6.5h16M9 6.5V4.8a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1.7M6.5 6.5l.8 13a1 1 0 0 0 1 .9h7.4a1 1 0 0 0 1-.9l.8-13"/></svg>' : '−'}</button>
              <span>${item.quantity}</span>
              <button type="button" data-dline-qty="${item.quantity + 1}" data-key="${esc(item.key)}" aria-label="${esc(Theme.strings.increase || '+')}">+</button>
            </div>
            <span class="dline__price">${formatMoney(item.final_line_price)}</span>
          </div>
        </div>
        <button type="button" class="dline__remove" data-dline-remove="${esc(item.key)}" aria-label="${esc(Theme.strings.remove || 'Remove')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>
`;
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.cart-drawer__panel');
    this.linesEl = this.querySelector('[data-drawer-lines]');
    this.emptyEl = this.querySelector('[data-drawer-empty]');
    this.footerEl = this.querySelector('[data-drawer-footer]');
    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') trapFocus(this.panel, e);
    };

    // Cart icon opens the drawer (unless the merchant chose "cart page")
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener && Theme.settings.cartIcon !== 'page') {
        e.preventDefault();
        this.open(opener);
      }
    });

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
      const qtyBtn = e.target.closest('[data-dline-qty]');
      if (qtyBtn) this.changeQty(qtyBtn.dataset.key, Number(qtyBtn.dataset.dlineQty));
      const removeBtn = e.target.closest('[data-dline-remove]');
      if (removeBtn) this.changeQty(removeBtn.dataset.dlineRemove, 0);
    });

    overlayEl()?.addEventListener('click', () => this.close());
    document.addEventListener('cart:updated', (e) => {
      if (this.isOpen()) this.render(e.detail.cart);
      else this.renderShipping(e.detail.cart); // bar is always current on next open
    });
    // Adding to cart must never be silent: when the upsell popup is disabled,
    // the drawer is the add-to-cart feedback.
    document.addEventListener('cart:added', () => {
      if (!Theme.settings.popupEnabled && Theme.settings.cartIcon !== 'page') this.open();
    });
  }

  isOpen() { return this.panel?.classList.contains('is-open'); }

  async open(trigger) {
    this.trigger = trigger;
    this.hidden = false;
    requestAnimationFrame(() => this.panel.classList.add('is-open'));
    lockScroll(true);
    document.addEventListener('keydown', this.onKey);
    setTimeout(() => this.querySelector('[data-drawer-close]')?.focus(), 120);
    try {
      const cart = Cart.state || await Cart.get();
      this.render(cart);
      Cart.get().then((fresh) => this.render(fresh)).catch(() => {});
    } catch {
      this.render({ items: [], item_count: 0, total_price: 0 });
    }
  }

  close() {
    if (!this.isOpen()) return;
    this.panel.classList.remove('is-open');
    unlockScroll();
    document.removeEventListener('keydown', this.onKey);
    setTimeout(() => { this.hidden = true; this.trigger?.focus?.(); }, 450);
  }

  async changeQty(key, quantity) {
    const line = this.linesEl.querySelector(`[data-line="${cssEscape(key)}"]`);
    line?.classList.add('is-updating');
    try {
      await Cart.change(key, quantity);
    } catch (err) {
      line?.classList.remove('is-updating');
      toast(err.message || Theme.strings.cartError);
    }
  }

  render(cart) {
    if (!cart) return;
    const count = this.querySelector('[data-drawer-count]');
    if (count) count.textContent = cart.item_count;
    const subtotal = this.querySelector('[data-drawer-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    const empty = !cart.items || cart.items.length === 0;
    if (this.emptyEl) this.emptyEl.hidden = !empty;
    if (this.footerEl) this.footerEl.hidden = empty;
    if (this.linesEl) this.linesEl.hidden = empty;
    this.renderShipping(cart);
    if (empty) { if (this.linesEl) this.linesEl.innerHTML = ''; return; }

    this.linesEl.innerHTML = cart.items.map(cartLineHTML).join('');
  }

  renderShipping(cart) {
    const bar = this.querySelector('[data-shipping-bar]');
    if (!bar) return;
    const threshold = Theme.settings.freeShippingThreshold || 0;
    if (!threshold || !Theme.settings.showShippingBar || !cart.items?.length) { bar.hidden = true; return; }
    bar.hidden = false;
    const remaining = Math.max(0, threshold - cart.total_price);
    const label = bar.querySelector('.shipping-bar__label');
    const fill = bar.querySelector('.shipping-bar__fill');
    if (fill) fill.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
    if (label) {
      if (remaining <= 0) {
        label.classList.add('is-unlocked');
        label.textContent = Theme.strings.freeShippingUnlocked;
      } else {
        label.classList.remove('is-unlocked');
        label.innerHTML = (Theme.strings.freeShippingAway || '[amount]').replace('[amount]', `<strong>${formatMoney(remaining)}</strong>`);
      }
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ----------------------------- <quick-add-drawer> -------------------------- */
/* Salomon-style slide-in panel opened from a product card's bag icon.
   Content (color row + option chips) is rendered by the card itself via
   renderOptionsInto, so state stays in one place. */
class QuickAddDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.quick-add-drawer__panel');
    this.body = this.querySelector('[data-qa-body]');
    this.addBtn = this.querySelector('[data-qa-add]');
    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') trapFocus(this.panel, e);
    };
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
    overlayEl()?.addEventListener('click', () => this.close());
    this.addBtn?.addEventListener('click', () => {
      const card = this.card;
      if (!card) return;
      const chosen = card.selectedVariant(card.panelGroups());
      if (!chosen || !chosen.available) return;
      // close first so the cart drawer / popup can take over the overlay
      this.close();
      card.add(chosen.id);
    });
  }

  isOpen() { return this.panel?.classList.contains('is-open'); }

  open(card) {
    this.card = card;
    this.trigger = document.activeElement;
    this.render();
    this.hidden = false;
    requestAnimationFrame(() => this.panel.classList.add('is-open'));
    lockScroll(true);
    document.addEventListener('keydown', this.onKey);
    setTimeout(() => this.querySelector('[data-drawer-close]')?.focus(), 120);
  }

  close() {
    if (!this.isOpen()) return;
    this.panel.classList.remove('is-open');
    unlockScroll();
    document.removeEventListener('keydown', this.onKey);
    setTimeout(() => { this.hidden = true; this.trigger?.focus?.(); }, 450);
  }

  render() {
    const card = this.card;
    if (!card || !this.body) return;

    const primary = card.querySelector('.product-card__img.is-primary');
    const img = this.querySelector('[data-qa-image]');
    if (img) {
      const src = primary?.currentSrc || primary?.src || '';
      img.src = src;
      img.hidden = !src;
    }
    const title = this.querySelector('[data-qa-title]');
    if (title) title.textContent = card.querySelector('.product-card__title')?.textContent.trim() || '';
    const price = this.querySelector('[data-qa-price]');
    if (price) price.textContent = card.querySelector('.price__current')?.textContent.trim() || '';
    const url = card.querySelector('a.product-card__link')?.href
      || card.querySelector('a.product-card__media-link')?.href || '';
    const details = this.querySelector('[data-qa-details]');
    if (details) {
      details.href = url || '#';
      details.hidden = !url;
    }

    this.body.innerHTML = '';
    card.renderOptionsInto(this.body, () => this.render());

    const chosen = card.selectedVariant(card.panelGroups());
    if (this.addBtn) this.addBtn.disabled = !(chosen && chosen.available);
  }
}
customElements.define('quick-add-drawer', QuickAddDrawer);

/* ----------------------------- <cart-page> --------------------------------- */
class CartPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('change', (e) => {
      const qty = e.target.closest('[data-line-qty]');
      if (qty) this.updateLine(qty.dataset.lineQty, parseInt(qty.value, 10) || 0);
      const note = e.target.closest('[data-cart-note]');
      if (note) this.saveNote(note.value);
    });
    this.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-line-remove]');
      if (remove) { e.preventDefault(); this.updateLine(remove.dataset.lineRemove, 0); }
    });
    // adds from outside the line list (mini upsell, popup) re-render the
    // whole section so lines, totals and the shipping bar match reality
    document.addEventListener('cart:added', () => this.refresh());
  }

  async updateLine(key, quantity) {
    this.querySelector(`[data-line-key="${cssEscape(key)}"]`)?.classList.add('is-updating');
    try {
      await Cart.change(key, quantity);
      await this.refresh();
    } catch (err) {
      toast(err.message || Theme.strings.cartError);
      this.querySelector(`[data-line-key="${cssEscape(key)}"]`)?.classList.remove('is-updating');
    }
  }

  async saveNote(note) {
    try {
      await fetchJSON(`${Theme.routes.cartUpdate || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch { /* non-critical */ }
  }

  async refresh() {
    if (!this.sectionId) { window.location.reload(); return; }
    const res = await fetch(`${window.location.pathname}?section_id=${this.sectionId}`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('.section');
    const mine = this.closest('.section');
    if (fresh && mine) mine.replaceWith(fresh);
  }
}
customElements.define('cart-page', CartPage);

/* ----------------------------- <localization-form> ------------------------ */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    const select = this.querySelector('select');
    select?.addEventListener('change', () => this.querySelector('form')?.submit());
  }
}
customElements.define('localization-form', LocalizationForm);

/* ----------------------------- <share-button> ----------------------------- */
class ShareButton extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', async () => {
      const data = { title: document.title, url: this.dataset.url || window.location.href };
      if (navigator.share) { navigator.share(data).catch(() => {}); }
      else {
        await navigator.clipboard.writeText(data.url).catch(() => {});
        toast(this.dataset.copied || 'Link copied');
      }
    });
  }
}
customElements.define('share-button', ShareButton);

/* ----------------------------- <hero-slider> ------------------------------ */
class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    if (!this.slides.length) return;
    this.index = 0;
    this.interval = parseInt(this.dataset.interval || 6500, 10);
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reducedMotion.matches;
    this.playing = this.autoplay;
    this.userPaused = false;
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];

    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    this.toggleBtn = this.querySelector('[data-hero-toggle]');
    this.toggleBtn?.addEventListener('click', () => this.toggleAutoplay());
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

    // Touch swipe
    let startX = null;
    this.addEventListener('pointerdown', (e) => { startX = e.clientX; }, { passive: true });
    this.addEventListener('pointerup', (e) => {
      if (startX == null) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 48) this.go(this.index + (dx < 0 ? 1 : -1), true);
      startX = null;
    });

    // Pause autoplay when off screen
    new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible && this.playing) this.schedule(); else this.clear();
      this.syncVideos();
    }, { threshold: 0.25 }).observe(this);

    this.activate(0);
    if (this.playing) this.schedule();
  }

  disconnectedCallback() { this.clear(); }

  clear() { clearTimeout(this.timer); this.dots[this.index]?.classList.remove('is-running'); }

  schedule() {
    this.clear();
    if (!this.playing || !this.visible || this.slides.length < 2) return;
    const dot = this.dots[this.index];
    if (dot) {
      dot.style.setProperty('--hero-progress-dur', `${this.interval}ms`);
      dot.classList.remove('is-running');
      void dot.offsetWidth;
      dot.classList.add('is-running');
    }
    this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
  }

  toggleAutoplay() {
    this.userPaused = !this.userPaused;
    this.playing = !this.userPaused && this.autoplay;
    this.toggleBtn?.setAttribute('aria-pressed', String(this.userPaused));
    this.toggleBtn?.classList.toggle('is-paused', this.userPaused);
    if (this.playing) this.schedule(); else this.clear();
    this.syncVideos();
  }

  go(i, manual = false) {
    const next = (i + this.slides.length) % this.slides.length;
    if (next === this.index) { if (manual) this.schedule(); return; }
    this.slides[this.index]?.classList.add('is-leaving');
    const prev = this.index;
    this.index = next;
    this.activate(next);
    setTimeout(() => this.slides[prev]?.classList.remove('is-leaving'), 900);
    this.schedule();
  }

  activate(i) {
    this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
    this.dots.forEach((d, n) => {
      d.classList.toggle('is-active', n === i);
      d.classList.remove('is-running');
      d.setAttribute('aria-selected', n === i ? 'true' : 'false');
    });
    this.syncVideos();
  }

  syncVideos() {
    this.slides.forEach((s, n) => {
      const video = s.querySelector('video');
      if (!video) return;
      const shouldPlay = n === this.index && this.visible !== false && !this.userPaused;
      if (shouldPlay) video.play().catch(() => {});
      else video.pause();
    });
  }
}
customElements.define('hero-slider', HeroSlider);

/* ----------------------------- <tech-explorer> ----------------------------- */
class TechExplorer extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-tech-slide]')];
    if (!this.slides.length) return;
    this.stage = this.querySelector('[data-tech-stage]');
    this.listBtns = [...this.querySelectorAll('[data-tech-goto]')];
    this.chips = [...this.querySelectorAll('[data-tech-chip]')];
    this.fill = this.querySelector('[data-tech-progress]');
    this.counter = this.querySelector('[data-tech-counter]');
    this.index = 0;
    this.autoplayMs = parseInt(this.dataset.autoplay || 0, 10);
    this.desktopMq = window.matchMedia('(min-width: 990px)');

    this.querySelector('[data-tech-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-tech-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    this.listBtns.forEach((b) => b.addEventListener('click', () => this.go(Number(b.dataset.techGoto), true)));
    this.chips.forEach((c) => c.addEventListener('click', () => this.go(Number(c.dataset.techChip), true)));

    // Mobile: stage is a scroll-snap track — sync index from scroll position
    this.stage?.addEventListener('scroll', debounce(() => {
      if (this.desktopMq.matches) return;
      const w = this.slides[0]?.getBoundingClientRect().width || 1;
      const gap = parseFloat(getComputedStyle(this.stage).columnGap || 0) || 0;
      const i = Math.round(this.stage.scrollLeft / (w + gap));
      if (i !== this.index) this.setActive(i, false);
    }, 80), { passive: true });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, true); }
    });

    new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible) this.schedule(); else this.clear();
      this.syncVideos();
    }, { threshold: 0.2 }).observe(this);

    this.desktopMq.addEventListener('change', () => this.setActive(this.index, false));
    ['pointerdown', 'focusin'].forEach((evt) => this.addEventListener(evt, () => { this.interacted = true; this.clear(); }));

    // The progress line doubles as a scrubber. Dragging moves RELATIVE to
    // the current slide (no jump); a plain click animates to the target.
    const track = this.fill?.parentElement;
    if (track) {
      let scrubbing = false;
      let scrubMoved = false;
      let scrubStartX = 0;
      let scrubStartIndex = 0;
      track.addEventListener('pointerdown', (e) => {
        scrubbing = true;
        scrubMoved = false;
        scrubStartX = e.clientX;
        scrubStartIndex = this.index;
        track.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      track.addEventListener('pointermove', (e) => {
        if (!scrubbing) return;
        const rect = track.getBoundingClientRect();
        if (!rect.width) return;
        scrubMoved = true;
        const step = rect.width / this.slides.length;
        const i = Math.max(0, Math.min(this.slides.length - 1,
          scrubStartIndex + Math.round((e.clientX - scrubStartX) / step)));
        if (i !== this.index) this.go(i, true);
      });
      const endScrub = (e) => {
        if (!scrubbing) return;
        scrubbing = false;
        if (!scrubMoved) {
          const rect = track.getBoundingClientRect();
          if (rect.width) {
            const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
            const i = Math.min(this.slides.length - 1, Math.floor(ratio * this.slides.length));
            if (i !== this.index) this.go(i, true);
          }
        }
      };
      track.addEventListener('pointerup', endScrub);
      track.addEventListener('pointercancel', endScrub);
    }

    this.setActive(0, false);
  }

  disconnectedCallback() { this.clear(); }
  clear() { clearTimeout(this.timer); }
  schedule() {
    this.clear();
    if (!this.autoplayMs || this.interacted || reducedMotion.matches || !this.visible) return;
    this.timer = setTimeout(() => { this.go(this.index + 1); this.schedule(); }, this.autoplayMs);
  }

  go(i, manual = false) {
    if (manual) { this.interacted = true; this.clear(); }
    const next = (i + this.slides.length) % this.slides.length;
    this.setActive(next, true);
  }

  setActive(i, scroll) {
    this.index = Math.max(0, Math.min(i, this.slides.length - 1));
    this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
    this.listBtns.forEach((b, n) => {
      b.classList.toggle('is-active', n === this.index);
      b.setAttribute('aria-selected', n === this.index ? 'true' : 'false');
    });
    this.chips.forEach((c, n) => c.classList.toggle('is-active', n === this.index));
    if (this.fill) this.fill.style.width = `${((this.index + 1) / this.slides.length) * 100}%`;
    if (this.counter) this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;

    // Scroll only the rail itself — scrollIntoView would scroll every
    // ancestor including the page, yanking the visitor to this section
    const activeList = this.listBtns[this.index];
    const rail = activeList?.parentElement;
    if (rail && rail.scrollHeight > rail.clientHeight) {
      const delta = activeList.getBoundingClientRect().top - rail.getBoundingClientRect().top;
      rail.scrollTo({
        top: rail.scrollTop + delta - rail.clientHeight / 2 + activeList.clientHeight / 2,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
    const activeChip = this.chips[this.index];
    activeChip?.parentElement?.scrollTo({
      left: activeChip.offsetLeft - activeChip.parentElement.clientWidth / 2 + activeChip.clientWidth / 2,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });

    if (scroll && this.stage && !this.desktopMq.matches) {
      const w = this.slides[0]?.getBoundingClientRect().width || 0;
      const gap = parseFloat(getComputedStyle(this.stage).columnGap || 0) || 0;
      this.stage.scrollTo({ left: this.index * (w + gap), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    this.syncVideos();
  }

  syncVideos() {
    this.slides.forEach((s, n) => {
      const video = s.querySelector('video');
      if (!video) return;
      if (n === this.index && this.visible) video.play().catch(() => {});
      else video.pause();
    });
  }
}
customElements.define('tech-explorer', TechExplorer);

/* ----------------------------- <cart-upsell> (cart page "Complete your kit") ---- */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-kit-add]');
      if (!btn) return;
      const card = btn.closest('[data-kit-item]');
      const select = card?.querySelector('[data-kit-variant]');
      const id = Number(select ? select.value : btn.dataset.kitAdd);
      if (!id) return;
      btn.classList.add('btn--loading');
      try {
        // open:true → cart:added → the cart page re-renders itself (lines,
        // totals and this strip minus the product that is now in the cart)
        await Cart.add({ id, quantity: 1 }, { open: true });
      } catch (err) {
        toast(err.message || Theme.strings.cartError);
        btn.classList.remove('btn--loading');
      }
    });
  }
}
customElements.define('cart-upsell', CartUpsell);

/* ----------------------------- <upsell-popup> ------------------------------ */
class UpsellPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.upsell-popup__panel');
    this.recsGrid = this.querySelector('[data-upsell-grid]');
    this.recsWrap = this.querySelector('[data-upsell-recs]');
    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') trapFocus(this, e);
    };
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-upsell-close]') || e.target.classList.contains('upsell-popup__backdrop')) this.close();
      const toggle = e.target.closest('[data-upsell-lines-toggle]');
      if (toggle) this.setLinesOpen(!this.linesOpen);
      const qtyBtn = e.target.closest('[data-dline-qty]');
      if (qtyBtn) this.changeQty(qtyBtn.dataset.key, Number(qtyBtn.dataset.dlineQty));
      const removeBtn = e.target.closest('[data-dline-remove]');
      if (removeBtn) this.changeQty(removeBtn.dataset.dlineRemove, 0);
    });
    document.addEventListener('cart:added', (e) => this.show(e.detail));
    document.addEventListener('cart:updated', (e) => { if (!this.hidden) this.renderTotals(e.detail.cart); });
  }

  async show({ item, cart, recommendHandles, sourceId }) {
    if (!Theme.settings.popupEnabled) return;
    if (document.querySelector('cart-page')) return; // cart page re-renders instead
    // cart summary: always folded behind the arrow when the popup opens
    this.setLinesOpen(false);
    this.renderAdded(item);
    this.renderTotals(cart);
    // Cross-sell products are resolved BEFORE the popup opens (capped at
    // 2.5 s) so it appears in its final shape: either with the
    // "Complete your kit" column or as a compact single-column card.
    // No skeletons, nothing that shows up and then vanishes.
    const picks = await Promise.race([
      this.loadRecs(sourceId || item.product_id, recommendHandles, cart),
      new Promise((resolve) => setTimeout(() => resolve([]), 2500)),
    ]);
    this.renderRecs(picks);
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    lockScroll(false);
    document.addEventListener('keydown', this.onKey);
    this.opener = document.activeElement;
    this.querySelector('[data-upsell-close]')?.focus();
  }

  renderRecs(picks) {
    if (!this.recsGrid) return;
    this.recsGrid.innerHTML = '';
    const has = picks.length > 0;
    if (this.recsWrap) this.recsWrap.hidden = !has;
    this.panel?.classList.toggle('has-recs', has);
    picks.forEach((p) => this.recsGrid.appendChild(this.buildItem(p)));
  }

  close() {
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKey);
    unlockScroll();
    setTimeout(() => { this.hidden = true; this.opener?.focus?.(); }, 340);
  }

  renderAdded(item) {
    if (!item) return;
    const img = this.querySelector('[data-upsell-added-image]');
    if (img) {
      img.src = item.image ? item.image.replace(/(\.[a-z]{3,4})(\?|$)/, '_300x$1$2') : '';
      img.alt = item.product_title || '';
    }
    const title = this.querySelector('[data-upsell-added-title]');
    if (title) title.textContent = item.product_title || '';
    const variant = this.querySelector('[data-upsell-added-variant]');
    if (variant) variant.textContent = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const price = this.querySelector('[data-upsell-added-price]');
    if (price) price.textContent = formatMoney(item.final_line_price ?? item.final_price ?? item.price);
  }

  renderTotals(cart) {
    if (!cart) return;
    const count = this.querySelector('[data-upsell-count]');
    if (count) count.textContent = cart.item_count;
    const total = this.querySelector('[data-upsell-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
    const aff = this.querySelector('[data-upsell-aff]');
    if (aff && cart.attributes) {
      const code = String(cart.attributes.affiliate_code || '').trim();
      aff.hidden = !code;
      const codeEl = aff.querySelector('[data-upsell-aff-code]');
      if (codeEl) codeEl.textContent = code;
    }
    this.renderLines(cart);
    this.renderShipping(cart);
  }

  /* Cart summary inside the popup: every line with +/- and remove */
  renderLines(cart) {
    const wrap = this.querySelector('[data-upsell-lines]');
    const list = this.querySelector('[data-upsell-lines-list]');
    if (!wrap || !list) return;
    const items = cart.items || [];
    wrap.hidden = items.length === 0;
    list.innerHTML = items.map(cartLineHTML).join('');
    this.setLinesOpen(this.linesOpen);
  }

  setLinesOpen(open) {
    this.linesOpen = !!open;
    const toggle = this.querySelector('[data-upsell-lines-toggle]');
    const list = this.querySelector('[data-upsell-lines-list]');
    if (toggle) toggle.setAttribute('aria-expanded', this.linesOpen ? 'true' : 'false');
    if (list) list.hidden = !this.linesOpen;
  }

  async changeQty(key, quantity) {
    const line = this.querySelector(`[data-upsell-lines-list] [data-line="${cssEscape(key)}"]`);
    line?.classList.add('is-updating');
    try {
      await Cart.change(key, quantity); // cart:updated re-renders totals + lines
    } catch (err) {
      line?.classList.remove('is-updating');
      toast(err.message || Theme.strings.cartError);
    }
  }

  renderShipping(cart) {
    const bar = this.querySelector('[data-shipping-bar]');
    if (!bar) return;
    const threshold = Theme.settings.freeShippingThreshold || 0;
    if (!threshold || !Theme.settings.showShippingBar) { bar.hidden = true; return; }
    bar.hidden = false;
    const remaining = Math.max(0, threshold - cart.total_price);
    const label = bar.querySelector('.shipping-bar__label');
    const fill = bar.querySelector('.shipping-bar__fill');
    if (fill) fill.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
    if (label) {
      if (remaining <= 0) {
        label.classList.add('is-unlocked');
        label.textContent = Theme.strings.freeShippingUnlocked;
      } else {
        label.classList.remove('is-unlocked');
        label.innerHTML = (Theme.strings.freeShippingAway || '[amount]').replace('[amount]', `<strong>${formatMoney(remaining)}</strong>`);
      }
    }
  }

  /* Resolves the cross-sell list (never throws). Priority:
     product metafield custom.upsell_products → Search & Discovery related
     products (shopify--discovery--product_recommendation.related_products)
     — both arrive as `handles` from the product form, in that order —
     → Shopify related recommendations API → default recommendations
     collection (theme settings). Products already in the cart are skipped. */
  async loadRecs(productId, handles, cart) {
    if (!this.recsGrid) return [];
    const limit = Theme.settings.popupRecsCount || 4;
    const inCart = new Set((cart?.items || []).map((i) => i.product_id));
    const seen = new Set([Number(productId)]);
    const picks = [];
    const push = (products) => {
      (products || []).forEach((p) => {
        if (picks.length >= limit || !p || seen.has(p.id) || inCart.has(p.id)) return;
        seen.add(p.id);
        picks.push(p);
      });
    };

    try {
      if (handles?.length) {
        const fromHandles = await Promise.all(
          handles.slice(0, limit + 2).map((h) => fetchJSON(`${Theme.routes.root || '/'}products/${h}.js`).catch(() => null))
        );
        push(fromHandles.filter(Boolean));
      }
      if (picks.length < limit && productId) {
        const data = await fetchJSON(`${Theme.routes.recommendations || '/recommendations/products.json'}?product_id=${productId}&limit=${limit + 4}&intent=related`).catch(() => null);
        push(data?.products);
      }
      if (picks.length < limit && Theme.settings.popupRecsCollection) {
        const data = await fetchJSON(`${Theme.routes.root || '/'}collections/${Theme.settings.popupRecsCollection}/products.json?limit=${limit + 6}`).catch(() => null);
        push((data?.products || []).map((p) => this.normalizeRest(p)));
      }
    } catch { /* best effort */ }
    return picks.slice(0, limit);
  }

  normalizeRest(p) {
    // /collections/x/products.json prices are strings in major units
    const toCents = (v) => (v == null ? null : Math.round(parseFloat(v) * 100));
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: `/products/${p.handle}`,
      featured_image: p.images?.[0]?.src,
      price: toCents(p.variants?.[0]?.price) ?? 0,
      compare_at_price: toCents(p.variants?.[0]?.compare_at_price),
      variants: (p.variants || []).map((v) => ({
        id: v.id,
        title: v.title,
        available: v.available !== false,
        price: toCents(v.price),
      })),
    };
  }

  buildItem(p) {
    const el = document.createElement('div');
    el.className = 'upsell-item';
    const image = p.featured_image || p.images?.[0] || '';
    const url = p.url && p.url.startsWith('/') ? p.url.split('?')[0] : `/products/${p.handle}`;
    // every size in garment order; sold-out sizes stay visible but disabled
    const sizeOf = (v) => String(v.title || '').split(' / ')[0].trim();
    const variants = [...(p.variants || [])].sort((a, b) => sizeRank(sizeOf(a)) - sizeRank(sizeOf(b)));
    const hasOptions = variants.length > 1;
    const firstAvailable = variants.find((v) => v.available !== false) || variants[0];
    const compare = p.compare_at_price && p.compare_at_price > p.price
      ? `<s class="price__compare">${formatMoney(p.compare_at_price)}</s>` : '';

    el.innerHTML = `
      <a class="upsell-item__media" href="${url}" tabindex="-1">${image ? `<img src="${this.resize(image, 400)}" alt="" loading="lazy">` : ''}</a>
      <a class="upsell-item__title" href="${url}">${this.escape(p.title)}</a>
      <div class="upsell-item__price price">${formatMoney(p.price)} ${compare}</div>
      ${hasOptions ? `<div class="upsell-item__sizes" role="radiogroup" aria-label="${this.escape(Theme.strings.chooseSize || 'Choose size')}">${variants.map((v) => `<button type="button" class="upsell-item__size" role="radio" data-variant="${v.id}" aria-checked="${firstAvailable && v.id === firstAvailable.id ? 'true' : 'false'}"${v.available === false ? ' disabled' : ''}>${this.escape(v.title)}</button>`).join('')}</div>` : ''}
      <button type="button" class="btn btn--sm btn--atc btn--full upsell-item__btn">${Theme.strings.addToCart || 'Add'}</button>
    `;

    const chips = [...el.querySelectorAll('.upsell-item__size')];
    chips.forEach((chip) => chip.addEventListener('click', () => {
      chips.forEach((c) => c.setAttribute('aria-checked', c === chip ? 'true' : 'false'));
    }));
    const btn = el.querySelector('.upsell-item__btn');
    btn.addEventListener('click', async () => {
      const chosen = el.querySelector('.upsell-item__size[aria-checked="true"]');
      const variantId = chosen ? Number(chosen.dataset.variant) : firstAvailable?.id;
      if (!variantId) return;
      btn.classList.add('btn--loading');
      try {
        await Cart.add({ id: variantId, quantity: 1 }, { open: false });
        btn.classList.remove('btn--loading');
        btn.textContent = `✓ ${Theme.strings.added || 'Added'}`;
        btn.disabled = true;
      } catch (err) {
        btn.classList.remove('btn--loading');
        toast(err.message || Theme.strings.cartError);
      }
    });
    return el;
  }

  resize(url, width) {
    return url.replace(/(\.[a-z]{3,4})(\?|$)/i, `_${width}x$1$2`);
  }

  escape(str = '') {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}
customElements.define('upsell-popup', UpsellPopup);

/* ----------------------------- <wishlist-grid> ------------------------------ */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.render();
    document.addEventListener('wishlist:updated', () => this.prune());
  }

  async render() {
    const handles = Wishlist.read();
    if (!handles.length) return this.showEmpty();
    const products = (await Promise.all(
      handles.map((h) => fetchJSON(`${Theme.routes.root || '/'}products/${h}.js`).catch(() => null))
    )).filter(Boolean);
    if (this.loading) this.loading.hidden = true;
    if (!products.length) return this.showEmpty();

    this.grid.innerHTML = '';
    products.forEach((p) => {
      const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]{3,4})(\?|$)/i, '_600x$1$2') : '';
      const esc = (s = '') => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const card = document.createElement('product-card');
      card.className = 'product-card';
      card.dataset.handle = p.handle;
      card.dataset.id = p.id;
      card.innerHTML = `
        <div class="product-card__media">
          <a class="product-card__media-link" href="${p.url.split('?')[0]}" tabindex="-1" aria-hidden="true">
            ${img ? `<img class="product-card__img is-primary" src="${img}" alt="${esc(p.title)}" loading="lazy">` : ''}
          </a>
          <button class="card-icon-btn product-card__wishlist" type="button" data-wishlist-toggle="${esc(p.handle)}" aria-pressed="true"></button>
        </div>
        <div class="product-card__info">
          <h3 class="product-card__title"><a href="${p.url.split('?')[0]}" class="product-card__link">${esc(p.title)}</a></h3>
          ${p.type ? `<p class="product-card__subtitle">${esc(p.type)}</p>` : ''}
          <div class="product-card__price"><span class="price"><span class="price__current">${formatMoney(p.price)}</span>${p.compare_at_price && p.compare_at_price > p.price ? ` <s class="price__compare">${formatMoney(p.compare_at_price)}</s>` : ''}</span></div>
        </div>`;
      const heartBtn = card.querySelector('[data-wishlist-toggle]');
      heartBtn.innerHTML = '<svg class="icon icon--heart" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.3 4.9 13a4.7 4.7 0 0 1 0-6.6 4.6 4.6 0 0 1 6.5 0l.6.6.6-.6a4.6 4.6 0 0 1 6.5 0 4.7 4.7 0 0 1 0 6.6Z"/></svg><svg class="icon icon--heart-filled" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.3 4.9 13a4.7 4.7 0 0 1 0-6.6 4.6 4.6 0 0 1 6.5 0l.6.6.6-.6a4.6 4.6 0 0 1 6.5 0 4.7 4.7 0 0 1 0 6.6Z"/></svg>';
      this.grid.appendChild(card);
    });
    Wishlist.sync();
  }

  prune() {
    const handles = new Set(Wishlist.read());
    this.grid?.querySelectorAll('product-card').forEach((card) => {
      if (!handles.has(card.dataset.handle)) {
        card.style.transition = 'opacity 0.3s, transform 0.3s';
        card.style.opacity = '0';
        card.style.transform = 'scale(0.96)';
        setTimeout(() => { card.remove(); if (!this.grid.children.length) this.showEmpty(); }, 320);
      }
    });
  }

  showEmpty() {
    if (this.loading) this.loading.hidden = true;
    if (this.empty) this.empty.hidden = false;
    if (this.grid) this.grid.hidden = true;
  }
}
customElements.define('wishlist-grid', WishlistGrid);

/* ----------------------------- <product-form> (ATC forms) ------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.button = this.form.querySelector('[type="submit"]');
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
  }

  async submit() {
    const id = Number(this.form.querySelector('[name="id"]')?.value);
    if (!id) return;
    const qty = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
    const props = {};
    this.form.querySelectorAll('[name^="properties["]').forEach((input) => {
      const key = input.name.slice(11, -1);
      if (input.type === 'checkbox' && !input.checked) return;
      if (input.value) props[key] = input.value;
    });
    const handles = (this.dataset.recommend || '').split(',').map((s) => s.trim()).filter(Boolean);
    this.button?.classList.add('btn--loading');
    try {
      await Cart.add(
        { id, quantity: qty, properties: Object.keys(props).length ? props : undefined },
        { recommendHandles: handles.length ? handles : null, sourceId: Number(this.dataset.productId) || null }
      );
      // visible success state on the button itself
      const label = this.button?.querySelector('[data-atc-text]');
      if (label) {
        const original = label.textContent;
        label.textContent = `✓ ${Theme.strings.added || 'Added'}`;
        setTimeout(() => { label.textContent = original; }, 1600);
      }
    } catch (err) {
      toast(err.message || Theme.strings.cartError);
    } finally {
      this.button?.classList.remove('btn--loading');
    }
  }
}
customElements.define('product-form', ProductForm);

/* ----------------------------- <scroll-spy-nav> ----------------------------- */
class ScrollSpyNav extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('[data-spy-link]')];
    const targets = [...document.querySelectorAll('[data-spy-target]')];
    if (!this.links.length || !targets.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const id = en.target.dataset.spyTarget;
        this.links.forEach((l) => l.classList.toggle('is-active', l.dataset.spyLink === id));
        const active = this.links.find((l) => l.dataset.spyLink === id);
        if (active) {
          active.parentElement.scrollTo({
            left: active.offsetLeft - active.parentElement.clientWidth / 2 + active.clientWidth / 2,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
          });
        }
      });
    }, { rootMargin: '-30% 0px -55% 0px' });
    targets.forEach((t) => observer.observe(t));
  }
}
customElements.define('scroll-spy-nav', ScrollSpyNav);

/* ----------------------------- <announcement-bar> -------------------------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    // Dismissal applies only while the close button is enabled — turning
    // the button off in settings brings the bar back for everyone
    const closeBtn = this.querySelector('[data-announcement-close]');
    this.dismissKey = `in8te:announcement:${(this.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80)}`;
    if (closeBtn) {
      let dismissed = false;
      try { dismissed = localStorage.getItem(this.dismissKey) === '1'; } catch { /* noop */ }
      if (dismissed && !window.Shopify?.designMode) {
        this.remove();
        window.dispatchEvent(new Event('resize')); // header re-measures --announcement-height
        return;
      }
      closeBtn.addEventListener('click', () => {
        try { localStorage.setItem(this.dismissKey, '1'); } catch { /* noop */ }
        this.remove();
        window.dispatchEvent(new Event('resize'));
      });
    }

    this.items = [...this.querySelectorAll('[data-announcement]')];
    if (this.items.length < 2) return;
    this.index = 0;
    this.interval = parseInt(this.dataset.interval || 5000, 10);
    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.addEventListener('mouseenter', () => clearTimeout(this.timer));
    this.addEventListener('mouseleave', () => this.schedule());
    this.schedule();
  }
  disconnectedCallback() { clearTimeout(this.timer); }
  schedule() {
    clearTimeout(this.timer);
    if (reducedMotion.matches) return;
    this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
  }
  go(i) {
    this.index = (i + this.items.length) % this.items.length;
    this.items.forEach((el, n) => el.classList.toggle('is-active', n === this.index));
    this.schedule();
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ----------------------------- <site-header> ------------------------------- */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('.section-header-sticky') || this;
    this.lastY = window.scrollY;
    this.accrued = 0;
    this.setHeights();
    window.addEventListener('resize', debounce(() => this.setHeights(), 150));
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.initMegaMenus();
  }

  setHeights() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    const announcement = document.querySelector('announcement-bar');
    document.documentElement.style.setProperty('--announcement-height', `${announcement ? announcement.offsetHeight : 0}px`);
  }

  onScroll() {
    const y = window.scrollY;
    const dy = y - this.lastY;
    this.lastY = y;
    this.classList.toggle('is-stuck', y > 24);

    if (this.megaOpen || document.body.classList.contains('no-scroll')) { this.showBar(); return; }
    if (y < this.offsetHeight * 1.6) { this.showBar(); this.accrued = 0; return; }

    // accumulate direction so tiny jitters don't toggle the bar
    this.accrued = Math.sign(dy) === Math.sign(this.accrued) ? this.accrued + dy : dy;
    if (this.accrued > 14) this.hideBar();
    else if (this.accrued < -14) this.showBar();
  }

  hideBar() {
    if (this.classList.contains('is-hidden')) return;
    this.classList.add('is-hidden');
    this.closeAllMega();
  }
  showBar() { this.classList.remove('is-hidden'); }

  initMegaMenus() {
    this.navItems = [...this.querySelectorAll('[data-nav-item]')];
    const fine = window.matchMedia('(pointer: fine)').matches;

    this.navItems.forEach((item) => {
      const trigger = item.querySelector('[data-nav-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!panel) return;

      if (fine) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          this.openTimer = setTimeout(() => this.openMega(item), 60);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.closeMega(item), 140);
        });
      }
      trigger?.addEventListener('click', (e) => {
        // Touch / keyboard: first activation opens the panel
        if (!item.classList.contains('is-open') && (!fine || e.pointerType === 'touch')) {
          e.preventDefault();
          this.closeAllMega();
          this.openMega(item);
        }
      });
      trigger?.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' && !item.classList.contains('is-open')) {
          if (e.key === 'ArrowDown') e.preventDefault();
        }
      });
      item.addEventListener('focusin', () => { this.closeAllMega(item); this.openMega(item); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) this.closeMega(item); });
    });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAllMega(); });
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeAllMega(); });
  }

  openMega(item) {
    if (!item.querySelector('[data-mega-panel]')) return;
    item.classList.add('is-open');
    item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'true');
    this.megaOpen = true;
    this.classList.add('has-mega-open');
  }
  closeMega(item) {
    item.classList.remove('is-open');
    item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'false');
    if (!this.querySelector('[data-nav-item].is-open')) {
      this.megaOpen = false;
      this.classList.remove('has-mega-open');
    }
  }
  closeAllMega(except) {
    this.navItems?.forEach((i) => { if (i !== except) this.closeMega(i); });
  }
}
customElements.define('site-header', SiteHeader);

/* ----------------------------- <nav-drawer> -------------------------------- */
class NavDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('.drawer');
    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') trapFocus(this.drawer, e);
    };
    document.querySelectorAll('[data-drawer-open="menu"]').forEach((btn) => {
      btn.addEventListener('click', () => (this.isOpen() ? this.close() : this.open(btn)));
    });
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
    document.addEventListener('click', (e) => {
      if (this.isOpen() && !this.drawer.contains(e.target) && !e.target.closest('[data-drawer-open]')) this.close();
    });
    // Submenu accordions
    this.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const li = btn.closest('li');
        const open = li.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open);
      });
    });
  }
  isOpen() { return this.drawer?.classList.contains('is-open'); }
  open(trigger) {
    this.trigger = trigger;
    trigger?.classList.add('is-active');
    trigger?.setAttribute('aria-expanded', 'true');
    this.drawer.classList.add('is-open');
    lockScroll(true);
    document.addEventListener('keydown', this.onKey);
    setTimeout(() => this.drawer.querySelector(FOCUSABLE)?.focus(), 120);
  }
  close() {
    if (!this.isOpen()) return;
    this.drawer.classList.remove('is-open');
    this.trigger?.classList.remove('is-active');
    this.trigger?.setAttribute('aria-expanded', 'false');
    unlockScroll();
    document.removeEventListener('keydown', this.onKey);
    this.trigger?.focus();
  }
}
customElements.define('nav-drawer', NavDrawer);

/* ----------------------------- <search-modal> ------------------------------- */
class SearchModal extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.defaultContent = this.querySelector('[data-search-default]');
    this.onKey = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') trapFocus(this, e);
    };
    document.querySelectorAll('[data-search-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(btn); });
    });
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-close]') || e.target.classList.contains('search-modal__backdrop')) this.close();
    });
    this.input?.addEventListener('input', debounce(() => this.query(), 240));
  }

  open(trigger) {
    this.trigger = trigger;
    this.hidden = false;
    void this.offsetHeight; // commit the display change first so the transition still plays
    this.classList.add('is-open');
    lockScroll(false);
    document.addEventListener('keydown', this.onKey);
    setTimeout(() => this.input?.focus(), 80);
  }
  close() {
    this.classList.remove('is-open');
    unlockScroll();
    document.removeEventListener('keydown', this.onKey);
    setTimeout(() => { this.hidden = true; this.trigger?.focus(); }, 300);
  }

  async query() {
    const q = this.input.value.trim();
    if (!Theme.settings.predictiveSearch) return;
    if (q.length < 2) {
      if (this.results) { this.results.hidden = true; this.results.innerHTML = ''; }
      if (this.defaultContent) this.defaultContent.hidden = false;
      return;
    }
    try {
      const url = `${Theme.routes.predictiveSearch || '/search/suggest'}.json?q=${encodeURIComponent(q)}&resources[type]=product,query,collection,page&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`;
      const data = await fetchJSON(url);
      if (!data?.resources?.results) throw new Error('no results payload');
      this.render(data.resources.results, q);
    } catch {
      // suggestions unavailable: still give a way to the full results page
      this.showAllLink(q);
    }
  }

  showAllLink(q) {
    if (!this.results) return;
    if (this.defaultContent) this.defaultContent.hidden = true;
    this.results.hidden = false;
    const esc = (s = '') => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const allUrl = `${Theme.routes.search || '/search'}?q=${encodeURIComponent(q)}`;
    this.results.innerHTML = `<a class="btn btn--secondary search-modal__all" href="${allUrl}">${esc((Theme.strings.resultsFor || 'See all results for "[terms]"').replace('[terms]', q))}</a>`;
  }

  render(results, q) {
    if (!this.results) return;
    const { products = [], queries = [], collections = [], pages = [] } = results;
    if (this.defaultContent) this.defaultContent.hidden = true;
    this.results.hidden = false;

    const esc = (s = '') => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const price = (p) => {
      const cents = Math.round(parseFloat(p.price || 0) * 100);
      return Number.isFinite(cents) ? formatMoney(cents) : '';
    };

    let html = '';
    if (queries.length) {
      html += `<div class="search-modal__group"><p class="search-modal__label">${esc(Theme.strings.suggestions)}</p><ul class="search-modal__queries" role="list">`;
      html += queries.map((s) => `<li><a class="chip" href="${s.url}">${s.styled_text || esc(s.text)}</a></li>`).join('');
      html += '</ul></div>';
    }
    if (products.length) {
      html += `<div class="search-modal__group"><p class="search-modal__label">${esc(Theme.strings.products)}</p><ul class="search-modal__products" role="list">`;
      const imgUrl = (u) => u + (u.includes('?') ? '&' : '?') + 'width=160';
      html += products.map((p) => `
        <li>
          <a class="search-result" href="${p.url}">
            <span class="search-result__media">${p.featured_image?.url ? `<img src="${imgUrl(p.featured_image.url)}" alt="" width="56" height="84" loading="lazy">` : ''}</span>
            <span class="search-result__info">
              <span class="search-result__title">${esc(p.title)}</span>
              ${p.product_type ? `<span class="search-result__type">${esc(p.product_type)}</span>` : ''}
              <span class="search-result__price">${price(p)}</span>
            </span>
          </a>
        </li>`).join('');
      html += '</ul></div>';
    }
    if (collections.length || pages.length) {
      html += '<div class="search-modal__group search-modal__group--links">';
      if (collections.length) {
        html += `<p class="search-modal__label">${esc(Theme.strings.collections)}</p><ul role="list">${collections.map((c) => `<li><a class="link-underline" href="${c.url}">${esc(c.title)}</a></li>`).join('')}</ul>`;
      }
      if (pages.length) {
        html += `<p class="search-modal__label">${esc(Theme.strings.pages)}</p><ul role="list">${pages.map((p) => `<li><a class="link-underline" href="${p.url}">${esc(p.title)}</a></li>`).join('')}</ul>`;
      }
      html += '</div>';
    }
    if (!html) {
      html = `<p class="search-modal__empty">${esc(Theme.strings.noResults)}</p>`;
    } else {
      const allUrl = `${Theme.routes.search || '/search'}?q=${encodeURIComponent(q)}`;
      html += `<a class="btn btn--secondary search-modal__all" href="${allUrl}">${esc((Theme.strings.resultsFor || 'See all results for "[terms]"').replace('[terms]', q))}</a>`;
    }
    this.results.innerHTML = html;
  }
}
customElements.define('search-modal', SearchModal);

/* ----------------------------- Scroll guard ------------------------------ */
/* A fresh page must open at the top. Nothing in the theme scrolls on load,
   but third-party app scripts can — snap back if the page moves shortly
   after load with zero user input. Skipped for anchors (#…), back/forward
   and reload (the browser restores position there on purpose) and the
   theme editor. */
{
  const nav = performance.getEntriesByType?.('navigation')[0];
  const freshNavigation = !nav || nav.type === 'navigate';
  if (!window.location.hash && freshNavigation && !window.Shopify?.designMode) {
    let userMoved = false;
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((evt) =>
      window.addEventListener(evt, () => { userMoved = true; }, { passive: true, once: true }));
    const guard = () => {
      if (!userMoved && window.scrollY > 0) window.scrollTo(0, 0);
    };
    window.addEventListener('load', () => {
      guard();
      setTimeout(guard, 150);
      setTimeout(guard, 450);
      setTimeout(guard, 900);
    });
  }
}

/* ----------------------------- <read-more> --------------------------------- */
/* Collapses long copy to a few lines with a Read more / Read less toggle.
   The toggle only appears when the text actually overflows. */
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-readmore-body]');
    this.btn = this.querySelector('[data-readmore-toggle]');
    if (!this.body || !this.btn) return;
    this.moreLabel = this.btn.dataset.more || 'Read more';
    this.lessLabel = this.btn.dataset.less || 'Read less';
    this.sync();
    window.addEventListener('resize', debounce(() => this.sync(), 200));
    this.btn.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      this.btn.textContent = open ? this.lessLabel : this.moreLabel;
      this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  sync() {
    if (this.classList.contains('is-open')) return;
    const overflows = this.body.scrollHeight - this.body.clientHeight > 4;
    this.btn.hidden = !overflows;
    this.classList.toggle('is-clamped', overflows);
    this.btn.textContent = this.moreLabel;
    this.btn.setAttribute('aria-expanded', 'false');
  }
}
customElements.define('read-more', ReadMore);

/* ----------------------------- Boot -------------------------------------- */
function boot(root = document) {
  initReveals(root);
  initVisibleVideos(root);
  Wishlist.sync();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();

document.addEventListener('shopify:section:load', (e) => boot(e.target));

/* ----------------------------- <affiliate-code> ---------------------------- */
/* Partner code typed in the cart: saved as the cart attribute affiliate_code
   (what the affiliate webhook reads on the order) and as the in8te_ref
   cookie, the same two places the ?ref= link snippet uses. */
class AffiliateCode extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-aff-form]');
    this.input = this.querySelector('[data-aff-input]');
    this.applied = this.querySelector('[data-aff-applied]');
    this.codeEl = this.querySelector('[data-aff-code]');
    this.error = this.querySelector('[data-aff-error]');
    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.apply(this.input?.value || ''); });
    this.querySelector('[data-aff-remove]')?.addEventListener('click', () => this.apply(''));
    document.addEventListener('cart:updated', (e) => {
      const attrs = e.detail?.cart?.attributes;
      if (attrs && 'affiliate_code' in attrs) this.render(attrs.affiliate_code || '');
    });
  }
  clean(v) { return String(v || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20); }
  async apply(raw) {
    const code = this.clean(raw);
    if (raw && code.length < 3) { if (this.error) this.error.hidden = false; this.input?.focus(); return; }
    if (this.error) this.error.hidden = true;
    this.classList.add('is-busy');
    try {
      const cart = await fetchJSON(`${Theme.routes.cartUpdate || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { affiliate_code: code } }),
      });
      const d = new Date(); d.setTime(d.getTime() + (code ? 30 : -1) * 864e5);
      document.cookie = `in8te_ref=${encodeURIComponent(code)}; expires=${d.toUTCString()}; path=/; SameSite=Lax; Secure`;
      this.render(code);
      emit('cart:updated', { cart });
      Theme.track?.('affiliate_code_apply', { applied: !!code });
    } catch (err) {
      toast(err.message || Theme.strings.cartError);
    } finally {
      this.classList.remove('is-busy');
    }
  }
  render(code) {
    if (this.codeEl) this.codeEl.textContent = code;
    if (this.applied) this.applied.hidden = !code;
    if (this.form) this.form.hidden = !!code;
    if (!code && this.input) this.input.value = '';
    // a code that arrived after page load (?ref= link) must not stay folded away
    const details = this.querySelector('.aff__details');
    if (code && details) details.open = true;
  }
}
customElements.define('affiliate-code', AffiliateCode);

/* ----------------------------- Analytics (GA4) ----------------------------- */
/* Every event goes through Theme.track(). It is a no-op unless the GA4
   setting is on. gtag() is stubbed in <head> and gtag.js arrives after the
   page has painted, so early events queue in dataLayer and nothing here can
   slow the page down or break it (every call is wrapped). */
const Analytics = {
  on: !!(Theme.settings.analytics && typeof window.gtag === 'function'),
  currency: Theme.settings.currency || 'GBP',
  lastCart: null,
  track(name, params = {}) {
    if (!this.on) return;
    try { window.gtag('event', name, params); } catch { /* never break the page */ }
  },
  money(cents) { return Math.round(Number(cents) || 0) / 100; },
  itemFromLine(item) {
    const out = {
      item_id: String(item.product_id || item.id || ''),
      item_name: item.product_title || item.title || '',
      price: this.money(item.final_price ?? item.price),
      quantity: item.quantity || 1,
    };
    if (item.variant_title && item.variant_title !== 'Default Title') out.item_variant = item.variant_title;
    if (item.vendor) out.item_brand = item.vendor;
    if (item.product_type) out.item_category = item.product_type;
    return out;
  },
  listName() {
    const h = document.querySelector('main h1, h1');
    return (h ? h.textContent.trim() : '') || document.title;
  },
  itemFromCard(card, index) {
    return {
      item_id: card.dataset.id || card.dataset.handle || '',
      item_name: card.querySelector('.product-card__title')?.textContent.trim() || card.dataset.handle || '',
      index,
      item_list_name: this.listName(),
    };
  },
  cartItems(cart) { return (cart?.items || []).map((i) => this.itemFromLine(i)); },
  cartValue(cart) { return this.money(cart?.total_price); },
  init() {
    if (!this.on) return;
    const self = this;

    // product lists (collection, search, sliders): first 24 cards on the page
    const cards = [...document.querySelectorAll('product-card')].slice(0, 24);
    if (cards.length) this.track('view_item_list', { item_list_name: this.listName(), items: cards.map((c, i) => this.itemFromCard(c, i)) });

    document.addEventListener('click', (e) => {
      const link = e.target.closest('product-card a[href*="/products/"]');
      if (link) {
        const card = link.closest('product-card');
        if (card) self.track('select_item', { item_list_name: self.listName(), items: [self.itemFromCard(card, 0)] });
      }
      if (e.target.closest('[data-cart-open]')) {
        self.track('view_cart', { currency: self.currency, value: self.cartValue(self.lastCart), items: self.cartItems(self.lastCart) });
      }
      const wl = e.target.closest('[data-wishlist-toggle]');
      if (wl && !wl.classList.contains('is-active')) self.track('add_to_wishlist', { items: [{ item_id: wl.dataset.wishlistToggle, item_name: wl.dataset.wishlistToggle }] });
      if (e.target.closest('[data-modal-open="SizeGuideModal"], [data-modal-open="DoublesSizeGuide"]')) self.track('size_guide_open', { page_path: location.pathname });
      const social = e.target.closest('a[href*="instagram.com"], a[href*="tiktok.com"], a[href*="youtube.com"], a[href*="whatsapp.com"], a[href*="strava.com"]');
      if (social) self.track('social_click', { link_url: social.href });
    }, { passive: true });

    document.addEventListener('submit', (e) => {
      const f = e.target;
      if (!(f instanceof HTMLFormElement)) return;
      if (f.getAttribute('role') === 'search') self.track('search', { search_term: f.querySelector('input[name="q"]')?.value || '' });
      if (f.querySelector('input[name="contact[email]"]') && !f.querySelector('textarea')) self.track('newsletter_signup', { form_id: f.id || f.className || 'newsletter' });
    });

    document.addEventListener('cart:added', (e) => {
      const { item, cart } = e.detail || {};
      if (cart) self.lastCart = cart;
      if (item) self.track('add_to_cart', { currency: self.currency, value: self.money((item.final_price ?? item.price) * (item.quantity || 1)), items: [self.itemFromLine(item)] });
    });
    document.addEventListener('cart:updated', (e) => {
      const cart = e.detail?.cart;
      if (!cart) return;
      const prev = self.lastCart;
      if (prev && prev.items) {
        const now = new Map((cart.items || []).map((i) => [i.key, i]));
        prev.items.forEach((was) => {
          const is = now.get(was.key);
          const gone = is ? was.quantity - is.quantity : was.quantity;
          if (gone > 0) {
            const line = self.itemFromLine(was); line.quantity = gone;
            self.track('remove_from_cart', { currency: self.currency, value: self.money((was.final_price ?? was.price) * gone), items: [line] });
          }
        });
      }
      self.lastCart = cart;
    });

    // quiet cart snapshot after load (for remove / view_cart values); cart page = view_cart
    const seed = () => fetchJSON(`${Theme.routes.cart || '/cart'}.js`).then((cart) => {
      self.lastCart = cart;
      if (document.querySelector('cart-page')) self.track('view_cart', { currency: self.currency, value: self.cartValue(cart), items: self.cartItems(cart) });
    }).catch(() => {});
    if (document.readyState === 'complete') setTimeout(seed, 1500); else window.addEventListener('load', () => setTimeout(seed, 1500));

    // product page (product.js dispatches product:view once it has parsed the product)
    document.addEventListener('product:view', (e) => {
      const { product, variant } = e.detail || {};
      if (!product) return;
      const price = self.money(variant?.price ?? product.price);
      const item = { item_id: String(product.id), item_name: product.title, price, quantity: 1 };
      if (variant?.title && variant.title !== 'Default Title') item.item_variant = variant.title;
      if (product.vendor) item.item_brand = product.vendor;
      if (product.type) item.item_category = product.type;
      self.track('view_item', { currency: self.currency, value: price, items: [item] });
    });
    document.addEventListener('variant:change', (e) => {
      const v = e.detail?.variant;
      if (v) self.track('select_variant', { item_id: String(v.product_id || ''), item_variant: v.title || '' });
    });
    document.addEventListener('doubles:kit_added', (e) => self.track('doubles_kit_add', e.detail || {}));
  },
};
Theme.track = (name, params) => Analytics.track(name, params);
Analytics.init();
